/**
 * Remotion's `interpolate`, without Remotion: the same edge behaviour (extend by default, clamp / wrap / identity on request) and
 * the same multi-segment mapping, so a value computed here matches one computed inside a Remotion component to the last bit.
 */
export type Extrapolate = "extend" | "clamp" | "wrap" | "identity";
export interface InterpolateOptions { extrapolateLeft?: Extrapolate; extrapolateRight?: Extrapolate; easing?: (t: number) => number }

function segment(input: number, inputMin: number, inputMax: number, outputMin: number, outputMax: number, left: Extrapolate, right: Extrapolate, easing: (t: number) => number): number {
  let x = input;
  if (x < inputMin) {
    if (left === "identity") return x;
    if (left === "clamp") x = inputMin;
    else if (left === "wrap") { const range = inputMax - inputMin; x = ((((x - inputMin) % range) + range) % range) + inputMin; }
  }
  if (x > inputMax) {
    if (right === "identity") return x;
    if (right === "clamp") x = inputMax;
    else if (right === "wrap") { const range = inputMax - inputMin; x = ((((x - inputMin) % range) + range) % range) + inputMin; }
  }
  if (outputMin === outputMax) return outputMin;
  const t = easing((x - inputMin) / (inputMax - inputMin));
  return t * (outputMax - outputMin) + outputMin;
}

export function interpolate(input: number, inputRange: readonly number[], outputRange: readonly number[], options: InterpolateOptions = {}): number {
  if (inputRange.length !== outputRange.length) throw new Error(`inputRange (${inputRange.length}) and outputRange (${outputRange.length}) must have the same length`);
  if (inputRange.length < 2) throw new Error("inputRange must have at least 2 elements");
  for (let i = 1; i < inputRange.length; i++) if (!(inputRange[i] > inputRange[i - 1])) throw new Error(`inputRange must be strictly monotonically increasing but got [${inputRange.join(",")}]`);
  for (const v of [...inputRange, ...outputRange]) if (!Number.isFinite(v)) throw new Error("interpolate ranges must contain only finite numbers");
  const left = options.extrapolateLeft ?? "extend", right = options.extrapolateRight ?? "extend", easing = options.easing ?? ((t) => t);
  let i = 1;
  for (; i < inputRange.length - 1; i++) if (inputRange[i] >= input) break;
  i -= 1;
  return segment(input, inputRange[i], inputRange[i + 1], outputRange[i], outputRange[i + 1], left, right, easing);
}

/** `interpolate` clamped at both ends: the shape nearly every use in the reel takes. */
export const clampMap = (input: number, inputRange: readonly number[], outputRange: readonly number[]): number =>
  interpolate(input, inputRange, outputRange, { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

export const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
